import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import * as ReactRouter from 'react-router-dom';
import styled from 'styled-components';

import { fetchOpenKybProcesses } from 'apis/legitimizer/kyb/api';
import * as api from 'apis/merchant-onboarding-service/api';
import { fetchMerchants } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { fetchVenues } from 'apis/restaurant-api/merchant-admin/venue/api';
import { useTelemetryViewEvent } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { hasPermissions, usePermissions } from 'common/wolt-permissions';
import { homepageView } from 'core/telemetry/avo/Avo';
import useIsEnterpriseUser from 'modules/enterprise/hooks/useIsEnterpriseUser';

import FrontPageTemplate from './FrontPageTemplate';
import EntitySearch from './Search/EntitySearch';
import Contracts from './banner-notifications/contracts/Contracts';
import DraftVenues from './banner-notifications/draft-venues/DraftVenues';
import UnverifiedMerchants from './banner-notifications/unverified-merchants/UnverifiedMerchants';
import Loading from '../../core/Loading';
import { useDraftVenuesGroupedByMerchantId } from '../venue-data-collection/DraftVenueListPage/useDraftVenues';

const Heading = styled(T.Heading2)`
  color: ${cbSemanticCssVars.textPrimary};
  line-height: 52px;
`;

const Subtitle = styled.p`
  color: ${cbSemanticCssVars.textPrimary};
  line-height: 24px;
`;

const Warning = styled(Alert).attrs({ variant: 'warning' })`
  max-width: 600px;
`;

const NotificationBannersContainer = styled.div`
  margin-block-end: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FrontPage = () => {
  const {
    data: merchants,
    isLoading: isLoadingMerchants,
    error: merchantsError,
  } = useQuery(['/v1/merchant-admin/merchants'], () => fetchMerchants(), {
    // Explicitly refetching on focus is necessary because we can't integrate the Juro signature into merchant-admin.
    // Once the contract is signed in Juro a new merchant is created and the user should be able to see it when coming back to the app.
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });

  const { isEnterpriseUser, isLoading: isLoadingEnterpriseUser } = useIsEnterpriseUser();
  const isVenueFetchEnabled = !isLoadingEnterpriseUser && !isEnterpriseUser;
  const {
    data: venues,
    isLoading: isLoadingVenues,
    error: venuesError,
  } = useQuery(['/v1/merchant-admin/venues'], () => fetchVenues(), {
    enabled: isVenueFetchEnabled,
    initialData: [],
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const merchantIds = merchants?.map(m => m.itemid.$oid);
  const { data: openKybProcesses, isLoading: isLoadingKybProccesses } = useQuery(
    ['/v1/kyb/merchants/processes/latest', merchantIds],
    () => {
      if (!merchantIds) {
        // Should not end up here, because the query is disabled until merchants have been fetched.
        throw new Error('Merchant IDs not fetched');
      }
      return fetchOpenKybProcesses(merchantIds);
    },
    {
      enabled: !!merchantIds,
      // Set cacheTime to zero to ensure that the user does not get redirected again to KYB after completing it.
      cacheTime: 0,
    },
  );

  const { data: userContracts = [], isLoading: isLoadingContracts } = useQuery(['me/contracts'], () =>
    api.getUserContractDetails(),
  );
  const { data: onboardingStatusData, isLoading: isFetchingOnboardingStatus } = useQuery(
    ['merchant-admin/me/onboarding-status'],
    () => api.getOnboardingStatus(),
  );

  const { data: draftVenuesGroupedByMerchantId } = useDraftVenuesGroupedByMerchantId(merchantIds || [], [
    'BLANK',
    'READY_TO_SUBMIT',
  ]);

  const isLoading =
    isFetchingOnboardingStatus ||
    (isVenueFetchEnabled && isLoadingVenues) ||
    isLoadingMerchants ||
    isLoadingContracts ||
    isLoadingKybProccesses;

  const permissions = usePermissions();

  const unverifiedMerchants =
    merchants?.filter(merchant => openKybProcesses?.find(process => merchant.itemid.$oid === process.merchantId)) || [];

  const {
    shouldRenderContracts,
    shouldRenderUnverifiedMerchants,
    shouldRenderDraftVenuesSetupList,
    shouldRenderNotifications,
  } = useMemo(() => {
    const shouldRenderContracts = userContracts.length > 0;
    const shouldRenderUnverifiedMerchants = unverifiedMerchants.length > 0;
    const shouldRenderDraftVenuesSetupList = draftVenuesGroupedByMerchantId.length > 0;
    return {
      shouldRenderContracts,
      shouldRenderUnverifiedMerchants,
      shouldRenderDraftVenuesSetupList,
      shouldRenderNotifications:
        shouldRenderContracts || shouldRenderUnverifiedMerchants || shouldRenderDraftVenuesSetupList,
    };
  }, [userContracts.length, unverifiedMerchants.length, draftVenuesGroupedByMerchantId.length]);

  useTelemetryViewEvent('homepage-view', {}, durationMs => homepageView({ durationMs }));

  if (isLoading) {
    return <Loading />;
  }

  if (onboardingStatusData?.status === 'SSMO_CONTRACT_DATA_COLLECTION') {
    return <ReactRouter.Redirect to={`/onboarding-welcome?merchantId=${onboardingStatusData.merchantId}`} />;
  }

  if (onboardingStatusData?.status === 'SSMO_ONBOARDING_PAGE') {
    return <ReactRouter.Redirect to={`/onboarding-page?merchantId=${onboardingStatusData.merchantId}`} />;
  }

  if (merchantsError || venuesError) {
    return (
      <FrontPageTemplate>
        <Heading>
          <FormattedMessage id="frontpage.header" />
        </Heading>
        <Subtitle>
          <FormattedMessage id="frontpage.description" />
        </Subtitle>
        <Warning variant="warning">
          <FormattedMessage id="errors.data-fetch-failed-degraded-experience" />
        </Warning>
      </FrontPageTemplate>
    );
  }

  if (!merchants || (isVenueFetchEnabled && !venues) || !openKybProcesses) {
    return null;
  }

  if (merchants.length === 1 && openKybProcesses.length === 1) {
    return <ReactRouter.Redirect to={`/merchant/${openKybProcesses[0].merchantId}/business-verification/intro`} />;
  }

  const isSingleMerchantWithOnlyDraftVenues = merchants.length === 1 && draftVenuesGroupedByMerchantId.length === 1;
  // Redirect to venue data collection automatically to save the user clicking the draft venue data collection banner
  if (isSingleMerchantWithOnlyDraftVenues) {
    const [merchant] = draftVenuesGroupedByMerchantId;
    if (merchant.draftVenues.every(draftVenue => draftVenue.status !== 'VENUE_CREATED')) {
      return <ReactRouter.Redirect to={`venue-data-collection/${merchant.merchantId}`} />;
    }
  }

  const isEmployee = hasPermissions(
    [
      { resource: 'restaurant', operations: ['r'] },
      { resource: 'merchant', operations: ['r'] },
    ],
    permissions,
  );

  if (!isEmployee && !shouldRenderNotifications) {
    // Redirect to merchant view automatically to save the user clicking the merchant from the list
    const isSingleMerchantAndNoUnverifiedMerchants = unverifiedMerchants.length === 0 && merchants.length === 1;
    if (isSingleMerchantAndNoUnverifiedMerchants) {
      const merchantId = merchants[0].itemid.$oid;
      return <ReactRouter.Redirect to={`/merchant/${merchantId}`} />;
    }

    // Redirect to venue data collection automatically to save the user clicking the draft venue data collection banner
    const isSingleVenueAndNoDraftVenues = draftVenuesGroupedByMerchantId.length === 0 && venues.length === 1;
    if (isSingleVenueAndNoDraftVenues) {
      const venueId = venues[0].slug;
      return <ReactRouter.Redirect to={`/venue/${venueId}`} />;
    }
  }

  return (
    <FrontPageTemplate useNarrowMarginBlockStart={shouldRenderNotifications}>
      <Heading>
        <FormattedMessage id="frontpage.header" />
      </Heading>
      <Subtitle>
        <FormattedMessage id="frontpage.description" />
      </Subtitle>
      {shouldRenderNotifications && (
        <NotificationBannersContainer>
          {shouldRenderUnverifiedMerchants && <UnverifiedMerchants merchants={unverifiedMerchants} />}
          {shouldRenderDraftVenuesSetupList && (
            <DraftVenues draftVenuesGroupedByMerchant={draftVenuesGroupedByMerchantId} />
          )}
          {shouldRenderContracts && <Contracts userContracts={userContracts} />}
        </NotificationBannersContainer>
      )}
      <EntitySearch />
    </FrontPageTemplate>
  );
};

export default FrontPage;
